<template>
  <refresh-page-spinner v-if="isShowLoader" />
  <router-view />
</template>

<script>
import { SocialLogin } from '@capgo/capacitor-social-login';
import CreateEncryptionKeys from 'components/Modals/EncryptionKeysDialog.vue';
import RefreshPageSpinner from 'components/UI/RefreshPageSpinner';
import { arrayUnion } from 'firebase/firestore';
import { isEqual } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { Platform } from 'quasar';
import { useMobileMicrosoftAuth } from 'src/composables/auth';
import { useCacheCleaner } from 'src/composables/cacheCleaner';
import { useNativeAppUpdates } from 'src/composables/native-app-updates';
import { useNotifications } from 'src/composables/notifications';
import { useTime } from 'src/composables/time';
import { useClientsTriggerNotifications } from 'src/composables/triggerNotifications';
import RealTimeNotificationsDB from 'src/fb/realTimeNotifications-db';
import Auth from 'src/mixins/Auth';
import Interactions from 'src/mixins/Interactions';
import ServiceWorkerUpdate from 'src/mixins/ServiceWorkerUpdate';
import { auth } from 'src/store/auth';
import { broadcastChannel } from 'src/store/broadcastChannel';
import { network } from 'src/store/network';
import { programsTargets } from 'src/store/programsTargets';
import { remoteConfig } from 'src/store/remoteConfig';
import { userInterface } from 'src/store/userInterface';
import AppUtils from 'src/utils/AppUtils';

export default {
  name: 'App',

  components: { RefreshPageSpinner },

  mixins: [ServiceWorkerUpdate, Interactions, Auth],

  setup() {
    useClientsTriggerNotifications();
    const { setupPushNotifications, handleInAppNotificationReceived, setViewed } = useNotifications();
    const { initializeNativeAppUpdateFlow } = useNativeAppUpdates();
    const { initMobileMicrosoftAuthentication } = useMobileMicrosoftAuth();
    const { startCacheCleaner, stopCacheCleaner } = useCacheCleaner();
    const { initTimeComposable } = auth();
    initTimeComposable(useTime());

    return {
      setupPushNotifications,
      handleInAppNotificationReceived,
      setViewed,
      initializeNativeAppUpdateFlow,
      initMobileMicrosoftAuthentication,
      startCacheCleaner,
      stopCacheCleaner,
    };
  },

  data() {
    return {
      /*
        workflows notifications about to be handled bind to the Firestore.
        todo: move workflows notifications logic to the `triggerNotifications` composable
      */
      runWorkflowsNotifications: [],
      inAppNotifications: [],
      viewedInAppNotificationsIds: new Set(),
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'connectedClinic', 'userSettings', 'isShowLoader']),
    ...mapState(network, ['offlineMode']),
    ...mapState(userInterface, ['encryptionKeysDialogOpen', 'decryptionKeysDialogOpen']),

    // used in watcher to avoid deep watch
    newWorkflowsNotificationsTrigger() {
      return this.newWorkflowsNotifications.map((item) => item.id).toString();
    },

    // used in watcher to avoid deep watch
    newInAppNotificationsTrigger() {
      return this.newInAppNotifications.map((item) => item.id).toString();
    },

    newWorkflowsNotifications() {
      return this.runWorkflowsNotifications.filter((notification) => {
        return !(notification.viewedBy || []).includes(this.userDetails.userId);
      });
    },

    newInAppNotifications() {
      return this.inAppNotifications.filter((notification) => !notification.isViewed);
    },

    offlineModeStatus() {
      return JSON.parse(localStorage.getItem('offlineModeStatus')) || false;
    },

    /**
     * Returns the capacitor Google authentication configuration based on the environment.
     * We use EU client IDs for production and staging environments.
     *
     * @returns { iOSClientId: string, webClientId: string }
     */
    googleAuthConfig() {
      const isProduction = process.env.ENV_TYPE === 'production';

      if (isProduction) {
        return {
          iOSClientId: '538145091071-q9akkacrtd7mh1s70v91cb6hle2tbja7.apps.googleusercontent.com',
          webClientId: '538145091071-046b6rqol42f1aisf54a355k3o40tb81.apps.googleusercontent.com',
        };
      }

      return {
        iOSClientId: '284709617245-pbb7nd7ktfqvsiblgj34er799ge73g9k.apps.googleusercontent.com',
        webClientId: '284709617245-kidk4gfj61c4d2riemliks3t6duo465r.apps.googleusercontent.com',
      };
    },
  },

  watch: {
    'userSettings.offlineClients': {
      handler(value, prevValue) {
        const isValueDifferent = Array.isArray(value) && !isEqual(value, prevValue);

        if (!this.userHasPrivilege('offline/hasAccess') || !isValueDifferent || this.offlineMode) {
          return;
        }

        if (value.length === 0) {
          this.unbindRunWorkflowsNotifications();
        } else {
          this.bindRunWorkflowsNotifications();
        }
      },
    },

    'userDetails.userId': {
      handler(value, prevValue) {
        if (value === prevValue) {
          return;
        }

        if (value) {
          this.bindInAppNotifications();
        } else {
          this.unbindInAppNotifications();
        }
      },
    },

    async newWorkflowsNotificationsTrigger(value) {
      if (value.length === 0 || !this.userDetails.userId) {
        return;
      }

      const realTimeNotificationsDb = new RealTimeNotificationsDB();
      const actions = this.newWorkflowsNotifications.map(async (notification) => {
        await realTimeNotificationsDb.set({
          isViewed: true,
          viewedBy: arrayUnion(this.userDetails.userId),
        }, notification.id);
      });

      await AppUtils.allSettled(actions);
      await this.offlineSync({ ignoreDebounce: true });
    },

    async newInAppNotificationsTrigger(value) {
      if (value.length === 0 || !this.userDetails.userId) {
        return;
      }

      const notViewedNotifications = this.newInAppNotifications.filter((notification) => {
        return !this.viewedInAppNotificationsIds.has(notification.id);
      });

      // Trigger function can be called multiple times, so we need to check if there are any notifications left
      // to avoid recursion calls
      if (notViewedNotifications.length === 0) {
        return;
      }

      this.viewedInAppNotificationsIds = new Set([
        ...this.viewedInAppNotificationsIds,
        ...notViewedNotifications.map((notification) => notification.id),
      ]);

      const lastNotification = notViewedNotifications[notViewedNotifications.length - 1];

      this.handleInAppNotificationReceived(lastNotification);

      const realTimeNotificationsDb = new RealTimeNotificationsDB();
      const actions = notViewedNotifications.map(async (notification) => {
        await realTimeNotificationsDb.set({
          isViewed: true,
        }, notification.id);
      });

      await AppUtils.allSettled(actions, () => {
        this.viewedInAppNotificationsIds.clear();
      });
    },

    encryptionKeysDialogOpen(value) {
      if (!value || !this.userHasPrivilege('offline/hasAccess')) {
        return;
      }

      this.openComponentDialog(CreateEncryptionKeys, {}, null, null, null, {
        persistent: true,
      });
    },
  },

  async mounted() {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || savedTheme === 'light') {
      document.documentElement.setAttribute('data-theme', savedTheme);
    }

    this.offlineModeSetStatus(this.offlineModeStatus);

    this.networkSetStatus(navigator.onLine);
    window.addEventListener('online', this.setOnline);
    window.addEventListener('offline', this.setOffline);

    if (this.offlineModeStatus) {
      this.$router.push({ name: 'Encryption' });
    } else {
      this.handleAuthStateChanged({ context: this });
    }

    this.setupBroadcastChannel({ context: this });

    this.initializeNativeAppUpdateFlow();
    this.initMobileMicrosoftAuthentication();

    if (Platform.is.capacitor) {
      SocialLogin.initialize({
        google: this.googleAuthConfig,
      });
    }

    this.startCacheCleaner(this.invalidateProgramTargetsCache);
    await this.fetchConfig();
  },

  beforeUnmount() {
    this.stopCacheCleaner();

    window.removeEventListener('online', this.setOnline);
    window.removeEventListener('offline', this.setOffline);
  },

  methods: {
    ...mapActions(auth, ['handleAuthStateChanged', 'offlineSync']),
    ...mapActions(network, ['networkSetStatus', 'offlineModeSetStatus']),
    ...mapActions(broadcastChannel, ['setupBroadcastChannel']),
    ...mapActions(programsTargets, ['invalidateProgramTargetsCache']),
    ...mapActions(remoteConfig, ['fetchConfig']),

    setOnline() {
      this.networkSetStatus(true);
    },
    setOffline() {
      this.networkSetStatus(false);
    },

    async bindRunWorkflowsNotifications() {
      const realTimeNotificationsDb = new RealTimeNotificationsDB();
      await realTimeNotificationsDb.bindAll(this, 'runWorkflowsNotifications', [
        ['clinic', '==', this.connectedClinic.id],
        ['client', 'in', this.userSettings.offlineClients],
        ['context', '==', RealTimeNotificationsDB.context.OFFLINE_MODE],
        ['type', 'in', ['workflowsProceeded', 'noteTemplateUpdated', 'sessionStatusUpdated']],
      ], 'createTimestamp', 'asc');
    }, // bindRunWorkflowsNotifications()

    async unbindRunWorkflowsNotifications() {
      try {
        const realTimeNotificationsDb = new RealTimeNotificationsDB();
        await realTimeNotificationsDb.unbind(this, 'runWorkflowsNotifications');
      } catch (err) {
        // one possible error is when there is no bind at all, so it's not important
        console.error('Error unbinding notifications', err.message);
      }
    }, // unbindRunWorkflowsNotifications()

    async bindInAppNotifications() {
      const realTimeNotificationsDb = new RealTimeNotificationsDB();
      await realTimeNotificationsDb.bindAll(this, 'inAppNotifications', [
        ['user', '==', this.userDetails.userId],
        ['isInApp', '==', true],
        ['isViewed', '==', false],
      ], 'createTimestamp', 'asc');
    }, // bindInAppNotifications()

    async unbindInAppNotifications() {
      try {
        const realTimeNotificationsDb = new RealTimeNotificationsDB();
        await realTimeNotificationsDb.unbind(this, 'inAppNotifications');
      } catch (err) {
        // one possible error is when there is no bind at all, so it's not important
        console.error('Error unbinding notifications', err.message);
      }
    }, // unbindInAppNotifications()

  },
};
</script>
